import { useEffect, useId, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { MeResponse, Note } from "../../api/types";
import { hasPermission } from "../../auth/permissions";
import { formatDateTime } from "../../lib/format";

// NotesPanel is a small sidebar panel where a person keeps private reminders —
// e.g. "give Sam gumballs next season for covering my on-call." Notes are the
// author's own, persist across seasons, and can be edited or deleted once
// they've been acted on. A failed load is surfaced with a retry rather than
// silently masquerading as an empty list.
type LoadStatus = "loading" | "ready" | "error";

export function NotesPanel({ me }: { me: MeResponse }) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Note | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const noteID = useId();
  const canWrite = hasPermission(me, "notes:create");
  const overlayDismiss = useOverlayDismiss(cancelDelete);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    apiClient
      .listNotes()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setNotes(response.notes);
        setNextCursor(response.next_cursor);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function handleLoadMore() {
    if (nextCursor === undefined || loadingMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const response = await apiClient.listNotes({ cursor: nextCursor });
      setNotes((previous) => [...previous, ...response.notes]);
      setNextCursor(response.next_cursor);
    } catch {
      // Leave the button in place so the user can retry the next page.
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = text.trim();
    if (body === "" || submitting) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const note = await apiClient.createNote({ body });
      setNotes((previous) => [note, ...previous]);
      setText("");
      // Surface the new note even if the initial list load had failed.
      setStatus("ready");
    } catch (caught) {
      setError(messageFor(caught, "Couldn't save note."));
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(note: Note) {
    setEditingId(note.id);
    setEditText(note.body);
    setEditError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditText("");
    setEditError(null);
  }

  async function handleEditSubmit(event: React.FormEvent<HTMLFormElement>, note: Note) {
    event.preventDefault();
    const body = editText.trim();
    if (body === "" || editSubmitting) {
      return;
    }
    if (body === note.body) {
      cancelEdit();
      return;
    }
    setEditSubmitting(true);
    setEditError(null);
    try {
      const updated = await apiClient.updateNote(note.id, { body });
      setNotes((previous) => previous.map((n) => (n.id === updated.id ? updated : n)));
      cancelEdit();
    } catch (caught) {
      setEditError(messageFor(caught, "Couldn't update note."));
    } finally {
      setEditSubmitting(false);
    }
  }

  function requestDelete(note: Note) {
    setPendingDelete(note);
    setDeleteError(null);
  }

  function cancelDelete() {
    if (deleting) {
      return;
    }
    setPendingDelete(null);
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (pendingDelete === null || deleting) {
      return;
    }
    const id = pendingDelete.id;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiClient.deleteNote(id);
      setNotes((previous) => previous.filter((n) => n.id !== id));
      setPendingDelete(null);
    } catch (caught) {
      setDeleteError(messageFor(caught, "Couldn't delete note."));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className="notes-panel" aria-label="Notes">
      <header className="history__header">
        <h2 className="history__title">Notes</h2>
        <p className="history__sub">Jot down who to thank with gumballs next season.</p>
      </header>
      {canWrite && (
        <form className="notes-panel__form" onSubmit={handleSubmit}>
          <div className="form-row">
            <label htmlFor={noteID}>New note</label>
            <textarea
              id={noteID}
              rows={2}
              maxLength={300}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Someone did something nice…"
            />
          </div>
          {error !== null && <p className="form-row__error">{error}</p>}
          <button
            type="submit"
            className="btn btn--rainbow"
            disabled={text.trim() === "" || submitting}
          >
            {submitting ? "Saving…" : "Add note"}
          </button>
        </form>
      )}
      {status === "loading" && <p className="history__empty">Loading…</p>}
      {status === "error" && (
        <p className="form-row__error">
          Couldn't load your notes.{" "}
          <button
            type="button"
            className="notes-panel__retry"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Retry
          </button>
        </p>
      )}
      {status === "ready" &&
        (notes.length === 0 ? (
          <p className="history__empty">No notes yet.</p>
        ) : (
          <>
            <ul className="history__list">
              {notes.map((note) => (
                <li key={note.id} className="history__row notes-panel__row">
                  {editingId === note.id ? (
                    <form
                      className="notes-panel__editform"
                      onSubmit={(event) => handleEditSubmit(event, note)}
                    >
                      <div className="form-row">
                        <textarea
                          rows={2}
                          maxLength={300}
                          value={editText}
                          onChange={(event) => setEditText(event.target.value)}
                          aria-label="Edit note"
                          autoFocus
                        />
                      </div>
                      {editError !== null && <p className="form-row__error">{editError}</p>}
                      <div className="notes-panel__editactions">
                        <button
                          type="button"
                          className="notes-panel__action"
                          onClick={cancelEdit}
                          disabled={editSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn--rainbow btn--sm"
                          disabled={editText.trim() === "" || editSubmitting}
                        >
                          {editSubmitting ? "Saving…" : "Save"}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="history__row-text">
                        <span className="notes-panel__body">{note.body}</span>
                        <time>{formatDateTime(note.created_at)}</time>
                      </div>
                      {canWrite && (
                        <div className="notes-panel__actions">
                          <button
                            type="button"
                            className="notes-panel__action"
                            onClick={() => startEdit(note)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="notes-panel__action notes-panel__action--danger"
                            onClick={() => requestDelete(note)}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
            {nextCursor !== undefined && (
              <button
                type="button"
                className="btn btn--ghost btn--sm notes-panel__more"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading…" : "Load older notes"}
              </button>
            )}
          </>
        ))}
      {pendingDelete !== null && (
        <div className="modal-overlay" role="presentation" {...overlayDismiss}>
          <div
            className="modal notes-panel__confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-note-title"
          >
            <header className="modal__header">
              <h2 id="delete-note-title">Delete note?</h2>
              <button
                type="button"
                className="modal__close"
                aria-label="Close"
                onClick={cancelDelete}
                disabled={deleting}
              >
                ×
              </button>
            </header>
            <div className="modal__body">
              <p>Make sure you gave the gumballs to your special someone before deleting this note.</p>
              <p className="notes-panel__deletepreview">{pendingDelete.body}</p>
              {deleteError !== null && <p className="form-row__error">{deleteError}</p>}
              <footer className="modal__footer">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={cancelDelete}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => void confirmDelete()}
                  disabled={deleting}
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </footer>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function messageFor(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    if (caught.status === 503) {
      return "Service is temporarily unavailable — please retry.";
    }
    if (caught.code === "not_found") {
      return "That note no longer exists.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : fallback;
}
